import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { thumbCard } from '../utils/img';
import dayjs from 'dayjs';
import useBackRestore, { useScrollRestore } from '../hooks/useBackRestore';

const GOLD = '#B8892A';
const INK = '#1A1410';
const INK2 = '#302418';
const INK3 = '#6B5840';
const INK4 = '#A89070';
const BG = '#FAF8F5';
const BG2 = '#F4F1EB';
const BD2 = '#EAE5DC';
const RED = '#9B2335';
const BLUE = '#1A4F8C';
const GREEN = '#2A6645';

const FALLBACK_CNY_RATE = 0.043;

const CAT_MAP = {
  watch: { label: '钟表', icon: '⌚' },
  jewelry: { label: '珠宝', icon: '💎' },
  bag: { label: '箱包', icon: '👜' },
  art: { label: '艺术品', icon: '🎨' },
  other: { label: '其他', icon: '📦' },
};

const STATUS_MAP = {
  live:     { label: '竞拍中',   bg: RED,   color: '#fff' },
  preview:  { label: '预览中',   bg: BLUE,  color: '#fff' },
  upcoming: { label: '即将开拍', bg: GOLD,  color: '#fff' },
  settling: { label: '结算中',   bg: GREEN, color: '#fff' },
  ended:    { label: '已结束',   bg: '#999',color: '#fff' },
};

const TYPE_MAP = {
  watch: '钟表', jewelry: '珠宝', bag: '箱包',
  mixed: '综合', art: '艺术品', other: '其他',
};

function formatJPY(val) {
  if (val == null || val === 0) return '';
  if (val >= 10000) return `${(val / 10000).toFixed(1)}万`;
  return `¥${Number(val).toLocaleString()}`;
}

// Countdown hook
function useCountdown(endAt) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!endAt) return;
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('已结束'); return; }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      if (d > 0) setText(`${d}天${h}小时${String(m).padStart(2,'0')}分`);
      else setText(`${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endAt]);
  return text;
}

// Session card for inline PC panel
function SessionCard({ session, houseId, navigate, isMobile }) {
  const countdown = useCountdown(session.end_at);
  const isLive = session.status === 'live' || session.status === 'preview';
  const isUpcoming = session.status === 'upcoming';

  return (
    <div
      onClick={() => navigate(`/sessions/${session.id}/lots`)}
      style={{
        background: '#fff', border: `1px solid ${BD2}`, borderRadius: 10,
        padding: isMobile ? '12px 14px' : '16px 20px',
        cursor: 'pointer', transition: 'all .12s', position: 'relative',
      }}
      onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,.1)'; e.currentTarget.style.borderColor = GOLD; }}}
      onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = BD2; }}}
    >
      {/* 状态徽章已去掉：改用源站信息(场次名/明拍暗拍/日期)，不再显示我们算的 竞拍中/预览中/即将开拍 */}

      {/* Title */}
      <div style={{
        fontSize: isMobile ? 13 : 15, fontWeight: 600,
        color: isLive ? RED : INK, marginBottom: 6, paddingRight: 70,
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}>
        <span>{session.name_zh || session.name || `${session.house_name} 场次`}</span>
        {session.bid_type && (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, flexShrink: 0,
            ...(session.bid_type === 'open'
              ? { background: 'rgba(42,102,69,.1)', color: GREEN, border: '1px solid rgba(42,102,69,.2)' }
              : { background: 'rgba(140,90,10,.1)', color: '#8C5A0A', border: '1px solid rgba(140,90,10,.2)' }),
          }}>
            {session.bid_type === 'open' ? '明拍' : '暗拍'}
          </span>
        )}
      </div>

      <div style={{ height: 1, background: BD2, margin: '8px 0' }} />

      {/* Countdown / start time */}
      {(isLive || isUpcoming) && (
        <div style={{ marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: INK3 }}>
            {isLive ? '剩余时间：' : '开拍时间：'}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: session.start_at ? RED : INK3, fontFamily: 'var(--mono,monospace)' }}>
            {isLive ? countdown : (session.start_at ? dayjs(session.start_at).format('MM/DD HH:mm') : '时间待公布')}
          </span>
        </div>
      )}

      {/* Info */}
      <div style={{ fontSize: 12, color: INK3, lineHeight: 1.8 }}>
        <div>商品件数：<strong>{session.active_lot_count != null ? session.active_lot_count : (session.lot_count || 0)}</strong></div>
        {session.commission_note && (
          <div>手续费：{session.commission_note}</div>
        )}
        {session.end_at && (
          <div>结束时间：{dayjs(session.end_at).format('YYYY/MM/DD HH:mm')}</div>
        )}
      </div>
    </div>
  );
}

// Inline sessions panel (PC only)
function InlineSessionsPanel({ houseId, houseName, houseLogo, navigate, tab }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!houseId) return;
    setLoading(true);
    api.get(`/auction-houses/${houseId}/sessions-hub`)
      .then(res => setSessions(res.data || []))
      .catch(() => message.error('加载场次失败'))
      .finally(() => setLoading(false));
  }, [houseId]);

  // 正在竞拍 = 竞拍中(live) + 预览中(preview)：都有拍品、都能出价（暗拍下見期即可出价，出价窗口到结束时间）。
  //   另：即将开拍(upcoming)但**已上拍品**的场次也归这里（有拍品=可浏览/出价）。
  // 即将开始 = upcoming **且没拍品**：拍品还没上（0件）、只有开拍日期、不能出价。
  const hasLots = (s) => (s.active_lot_count != null ? s.active_lot_count : (s.lot_count || 0)) > 0;
  // 正在竞拍 = live/preview/upcoming 且**有拍品**(排除 0 件空壳,如 tokioka 间隙残留)。
  const liveSessions = sessions.filter(s => (s.status === 'live' || s.status === 'preview' || s.status === 'upcoming') && hasLots(s));
  const upcomingSessions = sessions.filter(s => s.status === 'upcoming' && !hasLots(s));
  // 按当前 tab 只显示对应分区：正在竞拍 tab 不显示"即将开始"的场次，即将开始 tab 不显示竞拍中的。all 两个都显示。
  const showLive = tab !== 'upcoming' && liveSessions.length > 0;
  const showUpcoming = tab !== 'live' && upcomingSessions.length > 0;

  return (
    <div style={{
      marginTop: 16,
      background: '#fff',
      border: `1.5px solid ${GOLD}`,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 4px 20px rgba(184,137,42,.12)',
    }}>
      {/* Panel header */}
      <div style={{
        padding: '14px 24px',
        borderBottom: `1px solid ${BD2}`,
        display: 'flex', alignItems: 'center', gap: 12,
        background: 'linear-gradient(135deg, rgba(184,137,42,0.06), rgba(184,137,42,0.01))',
      }}>
        {houseLogo && (
          <img src={houseLogo} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', border: `1px solid ${BD2}` }} />
        )}
        <span style={{ fontSize: 16, fontWeight: 700, color: INK }}>{houseName}</span>
        <span style={{ fontSize: 12, color: INK4, marginLeft: 4 }}>— 场次一览</span>
        <a
          href={`/auction-hub/${houseId}`}
          onClick={e => { e.preventDefault(); navigate(`/auction-hub/${houseId}`); }}
          style={{ marginLeft: 'auto', fontSize: 12, color: GOLD, textDecoration: 'none' }}
        >
          查看全部 ›
        </a>
      </div>

      <div style={{ padding: '20px 24px' }}>
        {loading ? (
          <div style={{ padding: '30px 0', textAlign: 'center' }}><Spin /></div>
        ) : (!showLive && !showUpcoming) ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: INK4, fontSize: 13 }}>
            {tab === 'upcoming' ? '暂无即将开始的场次' : tab === 'live' ? '暂无正在竞拍的场次' : '暂无进行中或即将开始的场次'}
          </div>
        ) : (
          <>
            {showLive && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: RED, marginBottom: 12 }}>
                  🔴 竞拍中（{liveSessions.length}）
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: liveSessions.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  gap: 12,
                }}>
                  {liveSessions.map(s => (
                    <SessionCard key={s.id} session={s} houseId={houseId} navigate={navigate} isMobile={false} />
                  ))}
                </div>
              </div>
            )}

            {showUpcoming && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: GOLD, marginBottom: 12 }}>
                  ⏰ 即将开始（{upcomingSessions.length}）
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: upcomingSessions.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                  gap: 12,
                }}>
                  {upcomingSessions.map(s => (
                    <SessionCard key={s.id} session={s} houseId={houseId} navigate={navigate} isMobile={false} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── SPA 内存缓存：拍场页从任何子页返回时,用上次数据的 initializer 直接 seed state,
//    首屏无需等 /hub 网络往返(stale-while-revalidate,返回即出画面)。跨路由(SPA 不重载
//    JS)有效;整页刷新则重新拉取。houses 按 `tab|category` 分桶,featured/汇率与筛选无关全局存。
const _hubMem = { houses: {}, catStats: null, featured: {}, cnyRate: null }; // featured 按分类缓存: { [category]: [...] }

export default function AuctionHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoggedIn } = useAuth();
  // 返回恢复:从会场/场次/详情页 back 回来时恢复 tab、PC 展开的拍行与滚动位置
  const [restored, saveListState] = useBackRestore();
  const [tab, setTab] = useState(() => restored?.tab || 'live');
  // 页内搜索栏:有词直达搜索结果,无词退回搜索页(等同头部放大镜)
  const [q, setQ] = useState('');
  const doSearch = () => navigate(q.trim() ? `/browse?keyword=${encodeURIComponent(q.trim())}` : '/search');
  const categoryFilter = searchParams.get('category') || '';
  // 首屏缓存键跟随挂载时的 tab(默认 'live',返回恢复时可能是别的 tab)
  const _initKey = `${restored?.tab || 'live'}|${categoryFilter}`;
  const [houses, setHouses] = useState(() => _hubMem.houses[_initKey] || []);
  const [catStats, setCatStats] = useState(() => _hubMem.catStats || []);
  const [featured, setFeatured] = useState(() => _hubMem.featured[categoryFilter] || []);
  const [cnyRate, setCnyRate] = useState(() => _hubMem.cnyRate || FALLBACK_CNY_RATE);
  // 有缓存 → 不显示骨架(直接回显上次数据);无缓存首访 → 显示骨架
  const [loading, setLoading] = useState(() => !_hubMem.houses[_initKey]);
  // PC inline expansion
  const [selectedHouse, setSelectedHouse] = useState(() => restored?.selectedHouse ?? null); // { id, name_zh, logo_url }

  // 状态一变就写快照;滚动位置由 useScrollRestore 持续记录
  useEffect(() => { saveListState({ tab, selectedHouse }); }, [saveListState, tab, selectedHouse]);
  useScrollRestore(restored, saveListState, !loading && houses.length > 0);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 拍行列表(/hub)：stale-while-revalidate。命中缓存先即时回显(首屏不等网络)，再后台刷新；
  // 未命中才显示骨架。单一 effect 依赖 [tab, categoryFilter]，同时覆盖挂载与切标签，无重复请求。
  useEffect(() => {
    const key = `${tab}|${categoryFilter}`;
    const catParam = categoryFilter ? `&category=${categoryFilter}` : '';
    const hit = _hubMem.houses[key];
    if (hit) {
      setHouses(hit);
      if (_hubMem.catStats) setCatStats(_hubMem.catStats);
      setLoading(false);
    } else {
      setLoading(true);
    }
    api.get(`/auction-houses/hub?status=${tab}${catParam}`)
      .then(res => {
        const hs = res.data.houses || [];
        const cats = res.data.categories || [];
        setHouses(hs); setCatStats(cats);
        _hubMem.houses[key] = hs; _hubMem.catStats = cats;
      })
      .catch(() => { if (!hit) message.error('加载失败'); })
      .finally(() => setLoading(false));
  }, [tab, categoryFilter]);

  // 为你推荐：随热门分类联动。点击热门分类 → categoryFilter 变 → 拉该分类 30 件推荐。
  // 命中分类缓存则先秒回，再后台刷新；到达前推荐区留空，可优雅降级。
  useEffect(() => {
    const cached = _hubMem.featured[categoryFilter];
    if (cached) setFeatured(cached);
    const catParam = categoryFilter ? `&category=${encodeURIComponent(categoryFilter)}` : '';
    api.get(`/lots/featured?limit=30${catParam}`)
      .then(res => { const f = res.data || []; setFeatured(f); _hubMem.featured[categoryFilter] = f; })
      .catch(() => {});
  }, [categoryFilter]);

  // 汇率：与 tab/category 无关，挂载取一次，FALLBACK_CNY_RATE 兜底。
  useEffect(() => {
    api.get('/exchange-rates/latest')
      .then(res => { const r = Number(res.data?.rate) || FALLBACK_CNY_RATE; setCnyRate(r); _hubMem.cnyRate = r; })
      .catch(() => {});
  }, []);

  const handleHouseClick = useCallback((h) => {
    if (isMobile) {
      navigate(`/auction-hub/${h.id}`);
    } else {
      // Toggle: click same house again to collapse
      setSelectedHouse(prev => prev?.id === h.id ? null : h);
    }
  }, [isMobile, navigate]);

  const handleBid = (e, lotId) => {
    e.stopPropagation();
    if (!isLoggedIn) {
      message.info('请先登录后再参与竞拍');
      navigate('/login');
      return;
    }
    navigate(`/lots/${lotId}`);
  };

  // 不再整页 Spin 卡住首屏：静态骨架(标题/标签/导航)立即渲染，房屋网格用骨架块占位。
  const houseGridCols = isMobile ? 5 : 8;
  const featuredCols = isMobile ? 2 : 5;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 80 }}>
      <style>{`@keyframes hubPulse{0%,100%{opacity:1}50%{opacity:.45}}`}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '0' : '24px 32px 0' }}>

        {/* Desktop header */}
        {!isMobile && (
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: INK, margin: 0, letterSpacing: 1 }}>拍场总览</h1>
            <p style={{ fontSize: 13, color: INK4, margin: '4px 0 0' }}>日本拍卖会实时同步 · 专业代拍服务</p>
          </div>
        )}

        {/* Mobile header */}
        {isMobile && (
          <div style={{ padding: '16px 16px 10px', background: '#fff', borderBottom: `1px solid ${BD2}` }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: INK, margin: 0 }}>代拍</h1>
            <p style={{ fontSize: 12, color: INK4, margin: '4px 0 0' }}>日本拍卖会实时同步 · 专业代拍服务</p>
          </div>
        )}

        {/* 搜索栏(代拍下方,右侧放大镜即搜索按钮) */}
        <div style={{ padding: isMobile ? '12px 12px 0' : '0 0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: `1px solid ${BD2}`, borderRadius: 24, height: 42, paddingLeft: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
              placeholder="搜索品牌 / 关键词，如 劳力士、爱马仕"
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 14, background: 'transparent', color: INK }}
            />
            <button onClick={doSearch} aria-label="搜索" style={{ width: 50, height: 42, border: 'none', background: GOLD, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
              <SearchOutlined />
            </button>
          </div>
        </div>

        {/* 全部拍行 card */}
        <div style={{
          margin: isMobile ? '12px 12px 0' : '0 0 0',
          background: '#fff', borderRadius: isMobile ? 10 : 12,
          border: `1px solid ${BD2}`, overflow: 'hidden',
        }}>
          <div style={{ padding: isMobile ? '12px 14px 8px' : '18px 24px 12px', display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 12 }}>
            <span style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: INK }}>全部拍行</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <div onClick={() => navigate('/session-calendar')} style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                padding: isMobile ? '7px 10px' : '10px 14px', borderRadius: 8, cursor: 'pointer',
                background: 'linear-gradient(135deg, rgba(184,137,42,0.08), rgba(184,137,42,0.02))',
                border: `1px solid ${BD2}`,
              }}>
                <span style={{ fontSize: isMobile ? 15 : 18 }}>📅</span>
                <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 500, color: INK2 }}>{new Date().getMonth() + 1}月拍卖日历</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: INK4 }}>›</span>
              </div>
              <div onClick={() => navigate('/auction-fees')} style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                padding: isMobile ? '7px 10px' : '10px 14px', borderRadius: 8, cursor: 'pointer',
                background: 'linear-gradient(135deg, rgba(26,79,140,0.08), rgba(26,79,140,0.02))',
                border: `1px solid ${BD2}`,
              }}>
                <span style={{ fontSize: isMobile ? 15 : 18 }}>📋</span>
                <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 500, color: INK2 }}>出品手续费明细</span>
                <span style={{ marginLeft: 'auto', fontSize: 12, color: INK4 }}>›</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ padding: isMobile ? '0 14px 10px' : '0 24px 14px', display: 'flex', gap: 10 }}>
            {[
              { key: 'live', label: '正在竞拍', icon: '🔥' },
              { key: 'upcoming', label: '即将开始', icon: '⏰' },
              { key: 'all', label: '所有拍卖', icon: '📋' },
            ].map(t => (
              <button key={t.key} onClick={() => { setTab(t.key); setSelectedHouse(null); }} style={{
                padding: isMobile ? '5px 14px' : '7px 20px',
                borderRadius: 20, border: 'none', cursor: 'pointer',
                fontSize: isMobile ? 12 : 13, fontWeight: tab === t.key ? 600 : 400,
                background: tab === t.key ? GOLD : BG2,
                color: tab === t.key ? '#fff' : INK3,
                transition: 'all .15s',
              }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* House grid */}
          <div style={{
            padding: isMobile ? '0 10px 14px' : '0 20px 20px',
            display: 'grid',
            gridTemplateColumns: `repeat(${houseGridCols}, 1fr)`,
            gap: isMobile ? 6 : 10,
          }}>
            {houses.map(h => {
              const sessionCount = tab === 'live' ? h.live_sessions
                : tab === 'upcoming' ? h.upcoming_sessions
                : (h.live_sessions || 0) + (h.upcoming_sessions || 0); // 'all'：合计活跃场次
              const hasNew = sessionCount > 0; // 有活跃场次(控制高亮/透明度)
              // 2026-07-04 徽章优先级：LAST(快结束) > NEW(24h内上线) > 场次数字。
              // NEW/LAST 只在"正在竞拍/全部"视图有意义(有可出价拍品)。
              const showBadge = tab === 'live' || tab === 'all';
              const badgeLast = showBadge && hasNew && h.has_ending_soon;
              const badgeNew  = showBadge && hasNew && h.has_new && !h.has_ending_soon;
              const isSelected = !isMobile && selectedHouse?.id === h.id;
              return (
                <div key={h.id}
                  onClick={() => handleHouseClick(h)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    padding: isMobile ? '7px 2px' : '10px 4px',
                    cursor: 'pointer', borderRadius: 8,
                    transition: 'background .12s', position: 'relative',
                    opacity: (tab !== 'all' && houses.length && !hasNew) ? 0.4 : 1,
                    background: isSelected ? 'rgba(184,137,42,0.08)' : 'transparent',
                    outline: isSelected ? `2px solid ${GOLD}` : 'none',
                  }}
                  onMouseEnter={e => { if (!isMobile && !isSelected) e.currentTarget.style.background = BG2; }}
                  onMouseLeave={e => { if (!isMobile && !isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  {badgeLast ? (
                    <span style={{
                      position: 'absolute', top: 2, right: isMobile ? 2 : 6, fontSize: 8, fontWeight: 800,
                      letterSpacing: 0.3, background: RED, color: '#fff', padding: '1px 5px', borderRadius: 8,
                      boxShadow: '0 1px 3px rgba(155,35,53,.4)', zIndex: 1,
                    }}>LAST</span>
                  ) : badgeNew ? (
                    <span style={{
                      position: 'absolute', top: 2, right: isMobile ? 2 : 6, fontSize: 8, fontWeight: 800,
                      letterSpacing: 0.3, background: '#F5920A', color: '#fff', padding: '1px 5px', borderRadius: 8,
                      boxShadow: '0 1px 3px rgba(245,146,10,.4)', zIndex: 1,
                    }}>NEW</span>
                  ) : hasNew ? (
                    <span style={{
                      position: 'absolute', top: 2, right: isMobile ? 2 : 6, fontSize: 9, fontWeight: 700,
                      background: RED, color: '#fff', padding: '1px 5px', borderRadius: 8, zIndex: 1,
                    }}>{sessionCount}</span>
                  ) : null}
                  <div style={{
                    width: isMobile ? 40 : 52, height: isMobile ? 40 : 52, borderRadius: 12,
                    background: `linear-gradient(135deg, ${BG2}, #fff)`,
                    border: `1.5px solid ${isSelected ? GOLD : hasNew ? GOLD : BD2}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 6, overflow: 'hidden',
                  }}>
                    {h.logo_url ? (
                      <img src={h.logo_url} alt="" style={{ width: '70%', height: '70%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: GOLD }}>
                        {(h.code || h.name_zh || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span style={{
                    fontSize: isMobile ? 10 : 11, color: isSelected ? GOLD : INK2,
                    fontWeight: isSelected ? 600 : 500,
                    textAlign: 'center', lineHeight: 1.2, maxWidth: '100%',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {h.name_zh || h.code}
                  </span>
                  {h.active_lots > 0 && (
                    <span style={{ fontSize: 9, color: INK4, marginTop: 2 }}>{h.active_lots}件</span>
                  )}
                </div>
              );
            })}
            {/* 加载中且无缓存 → 骨架块占位(而非误导性的"暂无") */}
            {!houses.length && loading && (
              Array.from({ length: houseGridCols * 2 }).map((_, i) => (
                <div key={`sk-${i}`} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  padding: isMobile ? '7px 2px' : '10px 4px',
                }}>
                  <div style={{
                    width: isMobile ? 40 : 52, height: isMobile ? 40 : 52, borderRadius: 12,
                    background: BG2, marginBottom: 6, animation: 'hubPulse 1.2s ease-in-out infinite',
                  }} />
                  <div style={{ width: '68%', height: 8, borderRadius: 4, background: BG2, animation: 'hubPulse 1.2s ease-in-out infinite' }} />
                </div>
              ))
            )}
            {/* 加载完成仍无数据 → 真正的空态 */}
            {!houses.length && !loading && (
              <div style={{ gridColumn: '1/-1', padding: '24px 0', textAlign: 'center', color: INK4, fontSize: 13 }}>
                {tab === 'all' ? '暂无拍卖行' : `暂无${tab === 'live' ? '正在竞拍' : '即将开始'}的拍行`}
              </div>
            )}
          </div>
        </div>

        {/* PC inline sessions panel */}
        {!isMobile && selectedHouse && (
          <InlineSessionsPanel
            key={selectedHouse.id}
            houseId={selectedHouse.id}
            houseName={selectedHouse.name_zh || selectedHouse.code}
            houseLogo={selectedHouse.logo_url}
            navigate={navigate}
            tab={tab}
          />
        )}

        {/* 热门分类 */}
        {catStats.length > 0 && (
          <div style={{ margin: isMobile ? '12px 12px 0' : '24px 0 0' }}>
            <h2 style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: INK, margin: '0 0 10px' }}>热门分类</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(5, 1fr)',
              gap: isMobile ? 8 : 12,
            }}>
              {catStats.filter(c => c.category && CAT_MAP[c.category]).map(c => {
                const cat = CAT_MAP[c.category];
                const isActive = categoryFilter === c.category;
                return (
                  <div key={c.category}
                    onClick={() => {
                      if (isActive) {
                        setSearchParams({});
                      } else {
                        setSearchParams({ category: c.category });
                      }
                    }}
                    style={{
                      background: isActive ? 'rgba(184,137,42,.08)' : '#fff',
                      border: `1px solid ${isActive ? GOLD : BD2}`, borderRadius: isMobile ? 8 : 10,
                      padding: isMobile ? '10px 8px' : '14px 16px', cursor: 'pointer', transition: 'all .12s',
                      display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10,
                    }}
                    onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 2px 8px rgba(184,137,42,.15)'; }}}
                    onMouseLeave={e => { if (!isMobile && !isActive) { e.currentTarget.style.borderColor = BD2; e.currentTarget.style.boxShadow = 'none'; }}}
                  >
                    <span style={{ fontSize: isMobile ? 20 : 26 }}>{cat.icon}</span>
                    <div>
                      <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600, color: isActive ? GOLD : INK }}>{cat.label}</div>
                      <div style={{ fontSize: 10, color: INK4 }}>{c.count}件在拍</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 为你推荐 */}
        {featured.length > 0 && (
          <div style={{ margin: isMobile ? '16px 12px 0' : '24px 0 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 10 : 14 }}>
              <h2 style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: INK, margin: 0 }}>为你推荐</h2>
              <span onClick={() => navigate('/auction-hub')} style={{ fontSize: 12, color: GOLD, cursor: 'pointer' }}>更多 ›</span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${featuredCols}, 1fr)`,
              gap: isMobile ? 8 : 16,
            }}>
              {featured.map(lot => {
                const imgs = (() => { try { return typeof lot.image_urls === 'string' ? JSON.parse(lot.image_urls) : lot.image_urls; } catch { return []; } })();
                const img = imgs?.[0] || '';
                const price = lot.current_price_jpy || lot.start_price_jpy || 0;
                const cny = price ? Math.round(price * cnyRate) : 0;
                const diffMs = lot.end_at ? dayjs(lot.end_at).diff(dayjs()) : null;
                const diffSec = diffMs != null ? Math.floor(diffMs / 1000) : null;
                const isUrgent = diffSec != null && diffSec < 86400;
                const countdownText = (() => {
                  if (diffSec == null || diffSec <= 0) return '';
                  const d = Math.floor(diffSec / 86400);
                  const h = Math.floor((diffSec % 86400) / 3600);
                  const m = Math.floor((diffSec % 3600) / 60);
                  const s = diffSec % 60;
                  if (d > 0) return `${d}天${h}小时`;
                  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
                })();
                return (
                  <div key={lot.id} onClick={() => navigate(`/lots/${lot.id}`)} style={{
                    background: '#fff', border: `1px solid ${BD2}`, borderRadius: isMobile ? 8 : 10,
                    overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
                  }}
                    onMouseEnter={e => { if (!isMobile) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}}
                    onMouseLeave={e => { if (!isMobile) { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}}
                  >
                    <div style={{
                      height: isMobile ? 130 : 180,
                      background: BG2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      {img ? (
                        <img src={thumbCard(img)} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: 44 }}>📦</span>
                      )}
                      {lot.house_code && (
                        <span style={{
                          position: 'absolute', top: 6, right: 6, fontSize: 9, fontWeight: 600,
                          padding: '2px 6px', borderRadius: 3, background: 'rgba(0,0,0,0.5)', color: '#fff',
                        }}>{lot.house_name || lot.house_code}</span>
                      )}
                      {countdownText && (
                        <div style={{
                          position: 'absolute', bottom: 5, right: 5, fontFamily: 'var(--mono,monospace)',
                          fontSize: 9, padding: '2px 5px', borderRadius: 3,
                          background: isUrgent ? 'rgba(155,35,53,.85)' : 'rgba(26,20,16,.72)', color: '#fff',
                        }}>{countdownText}</div>
                      )}
                    </div>
                    <div style={{ padding: isMobile ? '8px 10px' : '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                        <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 9, color: INK4, letterSpacing: .8, textTransform: 'uppercase' }}>
                          {lot.brand || lot.category || ''}
                        </div>
                        {(lot.box_no || lot.lot_number) && (
                          <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 9, color: INK3, flexShrink: 0 }}>
                            No:{lot.box_no && lot.item_no
                              ? `${lot.box_no}-${String(lot.item_no).padStart(2, '0')}`
                              : (lot.box_no || lot.lot_number)}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: isMobile ? 11 : 12, fontWeight: 500, color: INK, lineHeight: 1.4, marginBottom: 6,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        minHeight: isMobile ? 30 : 34,
                      }}>{lot.title_zh || lot.title_ja}</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 6 }}>
                        <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: isMobile ? 13 : 14, fontWeight: 600, color: INK }}>
                          {formatJPY(price)}
                        </span>
                        {cny > 0 && <span style={{ fontSize: 10, color: INK4 }}>≈¥{cny.toLocaleString()}</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 10, color: INK4 }}>🔨 {lot.bid_count || 0}次出价</span>
                        <button
                          style={{
                            fontSize: 10, padding: isMobile ? '3px 7px' : '4px 12px', borderRadius: 3,
                            background: isLoggedIn ? 'rgba(184,137,42,.12)' : BG2,
                            color: isLoggedIn ? GOLD : INK3,
                            border: `1px solid ${isLoggedIn ? 'rgba(184,137,42,.2)' : BD2}`,
                            cursor: 'pointer', fontWeight: 500, transition: 'all .15s',
                          }}
                          onClick={e => handleBid(e, lot.id)}
                        >
                          {isLoggedIn ? '出价' : '登录出价'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
